import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Field, TextInput, SelectInput, TextArea, CheckboxRow, FormNotice } from "@/components/FormControls";
import CTAButton from "@/components/CTAButton";
import { allPlans } from "@/lib/plans";
import { useBrand } from "@/context/BrandContext";

const emptyForm = {
  parent_name: "",
  mobile: "",
  email: "",
  whatsapp_consent: false,
  child_name: "",
  child_age: "",
  age_group: "",
  school: "",
  area: "",
  pincode: "",
  preferred_plan: "",
  delivery_mode: "",
  vegetarian: "Vegetarian",
  has_allergies: "No",
  allergy_details: "",
  start_date: "",
  notes: "",
};

export const EarlyAccessForm = ({ title = "Start your plan", compact = false }) => {
  const brand = useBrand();
  const [form, setForm] = useState(emptyForm);
  const [state, setState] = useState({ status: "idle", message: "" });

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/leads/early-access", form);
      setState({ status: "done", message: data.message });
      setForm(emptyForm);
    } catch (err) {
      setState({
        status: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="early-access-form"
      className={compact ? "" : "mbm-card p-6 sm:p-8"}
    >
      <h3 className="font-heading text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-cocoa/80">
        No payment is taken on this site. {brand.serviceAreas.launchNote}
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label="Parent full name" required>
          <TextInput required value={form.parent_name} onChange={set("parent_name")} data-testid="ea-parent-name" placeholder="Your name" />
        </Field>
        <Field label="Mobile number" required>
          <TextInput required type="tel" value={form.mobile} onChange={set("mobile")} data-testid="ea-mobile" placeholder="+91" />
        </Field>
        <Field label="Email" required className="sm:col-span-2">
          <TextInput required type="email" value={form.email} onChange={set("email")} data-testid="ea-email" placeholder="you@email.com" />
        </Field>
        <Field label="Child’s name">
          <TextInput value={form.child_name} onChange={set("child_name")} data-testid="ea-child-name" />
        </Field>
        <Field label="Child’s age">
          <TextInput value={form.child_age} onChange={set("child_age")} data-testid="ea-child-age" placeholder="e.g. 6" />
        </Field>
        <Field label="Portion / age group">
          <SelectInput value={form.age_group} onChange={set("age_group")} data-testid="ea-age-group">
            <option value="">Select</option>
            {brand.ageRanges.map((a) => (
              <option key={a.id} value={a.id}>{`${a.label} — ${a.portion}`}</option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Child’s school">
          <TextInput value={form.school} onChange={set("school")} data-testid="ea-school" />
        </Field>
        <Field label="Area">
          <TextInput value={form.area} onChange={set("area")} data-testid="ea-area" placeholder="e.g. Baner" />
        </Field>
        <Field label="Pincode">
          <TextInput
            value={form.pincode}
            onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "") }))}
            maxLength={6}
            inputMode="numeric"
            data-testid="ea-pincode"
            placeholder="411045"
          />
        </Field>
        <Field label="Preferred plan">
          <SelectInput value={form.preferred_plan} onChange={set("preferred_plan")} data-testid="ea-plan">
            <option value="">Select</option>
            {allPlans().map((p) => (
              <option key={p.id} value={p.id}>{`${p.typeLabel} — ${p.durationLabel} (${p.totalMeals} meals)`}</option>
            ))}
            <option value="undecided">Not sure yet</option>
          </SelectInput>
        </Field>
        <Field label="Delivery preference">
          <SelectInput value={form.delivery_mode} onChange={set("delivery_mode")} data-testid="ea-delivery-mode">
            <option value="">Select</option>
            <option value="home">Home delivery</option>
            <option value="school">School delivery</option>
          </SelectInput>
        </Field>
        <Field label="Dietary preference">
          <SelectInput value={form.vegetarian} onChange={set("vegetarian")} data-testid="ea-dietary">
            <option value="Vegetarian">Vegetarian</option>
            <option value="Vegetarian without egg">Vegetarian, no egg</option>
            <option value="Jain">Jain</option>
            <option value="No onion or garlic">No onion or garlic</option>
          </SelectInput>
        </Field>
        <Field label="Does your child have any food allergies?">
          <SelectInput value={form.has_allergies} onChange={set("has_allergies")} data-testid="ea-has-allergies">
            <option value="No">No</option>
            <option value="Yes">Yes</option>
            <option value="Not sure">Not sure</option>
          </SelectInput>
        </Field>
        <Field label="Preferred start date">
          <TextInput type="date" value={form.start_date} onChange={set("start_date")} data-testid="ea-start-date" />
        </Field>
        {form.has_allergies === "Yes" && (
          <Field
            label="Allergy details"
            hint="Please contact our team before ordering if the allergy is severe or life-threatening."
            className="sm:col-span-2"
          >
            <TextArea value={form.allergy_details} onChange={set("allergy_details")} data-testid="ea-allergy-details" />
          </Field>
        )}
        <Field label="Anything else we should know?" className="sm:col-span-2">
          <TextArea value={form.notes} onChange={set("notes")} data-testid="ea-notes" rows={3} />
        </Field>
        <div className="sm:col-span-2">
          <CheckboxRow
            label="You may contact me on WhatsApp"
            description="We use WhatsApp for menu updates, delivery confirmations and support."
            checked={form.whatsapp_consent}
            onChange={set("whatsapp_consent")}
            data-testid="ea-whatsapp-consent"
          />
        </div>
      </div>

      {state.message && (
        <div className="mt-5">
          <FormNotice
            tone={state.status === "error" ? "error" : "info"}
            testId="early-access-message"
          >
            {state.message}
          </FormNotice>
        </div>
      )}

      <CTAButton
        type="submit"
        className="mt-6 w-full"
        disabled={state.status === "loading"}
        data-testid="early-access-submit"
      >
        {state.status === "loading" ? "Sending…" : "Start my plan"}
      </CTAButton>
    </form>
  );
};

export default EarlyAccessForm;
